import {
  BAGS_IMAGE_URL,
  BOXES_IMAGE_URL,
  LABELS_IMAGE_URL,
  formatFieldDisplayValue,
  formatProductOptionLabel,
} from './productChatConfig';

export const BAG_STYLE_GRID = { cols: 5, rows: 2, maxType: 10 };
export const BOX_STYLE_GRID = { cols: 5, rows: 3, maxType: 15 };

export const HEADER_IMAGE = `${process.env.PUBLIC_URL || ''}/logo/${encodeURIComponent('high quality Logo.png')}`;

const IMAGE_LOAD_TIMEOUT_MS = 15000;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      reject(new Error(`Timed out loading image: ${src}`));
    }, IMAGE_LOAD_TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`Could not load image: ${src}`));
    };
    img.src = src;
  });
}

function joinList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (typeof value === 'string') return value;
  return '';
}

function mmToInches(val) {
  if (val == null || val === '') return null;
  const n = parseFloat(String(val).replace(/[^\d.]/g, ''));
  if (Number.isNaN(n)) return String(val);
  return (n / 25.4).toFixed(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

export function getFinish(product) {
  const fields = product.fields || {};
  const raw = fields.printingFinishes || fields.finish || fields.finishes || fields.finishType || '';
  return formatFieldDisplayValue('printingFinishes', raw)
    || formatFieldDisplayValue('finish', raw)
    || raw;
}

export function getSpecialEffects(product) {
  const fields = product.fields || {};
  const parts = [
    joinList(fields.printingFeatures),
    joinList(fields.specialFeatures),
    joinList(fields.bagSpecifications),
    joinList(fields.specialEffects),
  ].filter(Boolean);
  return parts.join('; ');
}

export function getMaterialLine(product) {
  const fields = product.fields || {};
  const material = fields.material || '';
  return formatFieldDisplayValue('material', material) || material || fields.thickness || '';
}

export function getZipperLine(product) {
  const fields = product.fields || {};
  const raw = fields.zipperType || fields.closureType || '';
  return formatFieldDisplayValue('zipperType', raw) || raw;
}

export function getLoadingSide(product) {
  const bagType = product.fields?.bagType;
  if (product.productType === 'Bags' && ['2', '4', '7', '10'].includes(String(bagType))) {
    return 'Top';
  }
  return product.fields?.loadingSide || '—';
}

function getDimensionsInches(product) {
  const fields = product.fields || {};
  if (fields._dimensionsInInches && fields.width && fields.height) {
    const w = fields.width;
    const h = fields.height;
    const g = fields.gusset || fields.depth;
    return g ? `${w} W x ${h} H x ${g}` : `${w} W x ${h}`;
  }
  if (fields.width && fields.height) {
    const w = mmToInches(fields.width);
    const h = mmToInches(fields.height);
    const g = fields.gusset ? mmToInches(fields.gusset) : fields.depth ? mmToInches(fields.depth) : null;
    if (w && h && g) return `${w} W x ${h} H x ${g}`;
    if (w && h) return `${w} W x ${h}`;
  }
  if (fields.size?.widthMM && fields.size?.heightMM) {
    const w = mmToInches(fields.size.widthMM);
    const h = mmToInches(fields.size.heightMM);
    const g = fields.size.gussetMM ? mmToInches(fields.size.gussetMM) : null;
    if (w && h && g) return `${w} W x ${h} H x ${g}`;
    if (w && h) return `${w} W x ${h}`;
  }
  return '';
}

export function getProductSpecsText(product) {
  if (product.pdfSpecsLine) return product.pdfSpecsLine;

  const type = product?.productType;
  const dims = getDimensionsInches(product);

  if (type === 'Bags' && product.fields?.bagType) {
    const label = formatProductOptionLabel('bagType', String(product.fields.bagType));
    const short = label.replace(/^Stand Up with Bottom Gusset and Zipper/i, 'Stand up pouch');
    return dims ? `${short} ${dims}` : short;
  }

  if (type === 'Boxes' && product.fields?.boxType) {
    const label = formatProductOptionLabel('boxType', String(product.fields.boxType));
    const short = label.replace(/^#\d+\s*-\s*/, '');
    return dims ? `${short} ${dims}` : short;
  }

  return dims || getMaterialLine(product) || type || '—';
}

export function getSkuQuantityTiers(product) {
  const tiers = product.skuDetails?.[0]?.quantities?.map((q) => q.value).filter(Boolean);
  return tiers?.length ? tiers.join(', ') : '';
}

function imageToDataUrl(img) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/jpeg', 0.92);
}

export async function getFullStyleSheetWithHighlight(product) {
  const type = product?.productType;

  try {
    if (type === 'Bags') {
      const img = await loadImage(BAGS_IMAGE_URL);
      return imageToDataUrl(img);
    }

    if (type === 'Boxes') {
      const img = await loadImage(BOXES_IMAGE_URL);
      return imageToDataUrl(img);
    }

    if (type === 'Labels') {
      const img = await loadImage(LABELS_IMAGE_URL);
      return imageToDataUrl(img);
    }
  } catch (error) {
    console.warn('Style sheet image skipped:', error.message);
  }

  return null;
}

export function getStyleDetails(product) {
  const type = product?.productType;
  if (type === 'Bags') {
    const bagType = product.fields?.bagType || '4';
    return {
      stylePrefix: 'Style',
      styleNumber: bagType,
      styleLabel: formatProductOptionLabel('bagType', String(bagType)),
      material: getMaterialLine(product),
      loadingSide: getLoadingSide(product),
      zipper: getZipperLine(product),
      finish: getFinish(product),
    };
  }
  if (type === 'Boxes') {
    const boxType = product.fields?.boxType || '1';
    return {
      stylePrefix: 'Style',
      styleNumber: boxType,
      styleLabel: formatProductOptionLabel('boxType', String(boxType)),
      material: getMaterialLine(product),
      loadingSide: '—',
      zipper: '—',
      finish: getFinish(product),
    };
  }
  return {
    stylePrefix: 'Product',
    styleNumber: '',
    styleLabel: type || '—',
    material: getMaterialLine(product),
    loadingSide: '—',
    zipper: '—',
    finish: getFinish(product),
  };
}

export function getPrimaryShippingMethod(product) {
  return product.pdfShipping?.[0]?.method || product.fields?.shippingMethod || '';
}

export function getBoxPdfDetailLines(product) {
  const details = getStyleDetails(product);
  return [
    ['Box Style:', `#${details.styleNumber}`],
    ['Material:', details.material || ''],
    ['Finish:', details.finish || ''],
    ['Special Effects:', getSpecialEffects(product) || ''],
    ['How many SKUS:', String(product.SKU || '')],
    ['QTY per SKU:', getSkuQuantityTiers(product) || ''],
    ['Shipping method:', getPrimaryShippingMethod(product) || ''],
  ];
}

export function getLabelPdfDetailLines(product) {
  const fields = product.fields || {};
  const finish = getFinish(product);
  const special = fields.specialEffects || getSpecialEffects(product) || 'N/A';
  return [
    ['Material:', getMaterialLine(product) || ''],
    ['Thickness:', fields.thickness || ''],
    ['Finish:', finish || ''],
    ['Special Effects:', special],
    ['Rolling Direction:', fields.rollingDirection || fields.applyMethod || ''],
    ['Shipping method:', fields.shippingMethod || getPrimaryShippingMethod(product) || ''],
  ];
}

export function buildProductSpecsLine(product) {
  return getProductSpecsText(product);
}

export { BAGS_IMAGE_URL, BOXES_IMAGE_URL, LABELS_IMAGE_URL };
